import { useEffect, useMemo, useRef } from "react";

import type {
  TableCallAudioReadiness,
  TableCallStatus,
} from "./table-call.context.js";
import type { TableCallRuntime } from "./table-call.types.js";

export const useTableCallRuntime = (
  status: TableCallStatus,
  audioReadiness: TableCallAudioReadiness
): TableCallRuntime => {
  const activeTargetRef =
    useRef<TableCallRuntime["activeTargetRef"]["current"]>(null);
  const audioReadinessRef = useRef(audioReadiness);
  const audioRecoveryCountersRef = useRef({
    microphoneFallbackAttempts: 0,
    microphoneSameDeviceAttempts: 0,
    speakerFallbackAttempts: 0,
  });
  const desiredTargetRef =
    useRef<TableCallRuntime["desiredTargetRef"]["current"]>(null);
  const deviceApplyQueueRef = useRef(Promise.resolve());
  const isMountedRef = useRef(false);
  const isReconnectingRef = useRef(false);
  const lastRemoteFailureReportKeyRef = useRef<string | null>(null);
  const lifecycleQueueRef = useRef(Promise.resolve());
  const meetingRef = useRef<TableCallRuntime["meetingRef"]["current"]>(null);
  const microphoneToggleQueueRef = useRef(Promise.resolve());
  const operationGenerationRef = useRef(0);
  const reconnectReasonRef = useRef<string | null>(null);
  const reconnectRequestIdRef = useRef(0);
  const scheduleTransitionRef = useRef<() => void>(() => null);
  const statusRef = useRef(status);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);
  useEffect(() => {
    audioReadinessRef.current = audioReadiness;
  }, [audioReadiness]);

  return useMemo(
    () => ({
      activeTargetRef,
      audioReadinessRef,
      audioRecoveryCountersRef,
      desiredTargetRef,
      deviceApplyQueueRef,
      isMountedRef,
      isReconnectingRef,
      lastRemoteFailureReportKeyRef,
      lifecycleQueueRef,
      meetingRef,
      microphoneToggleQueueRef,
      operationGenerationRef,
      reconnectReasonRef,
      reconnectRequestIdRef,
      scheduleTransitionRef,
      statusRef,
    }),
    []
  );
};
